/**
 * SourcesSelect Component
 * Multi-select dropdown for load sources with connection status indicators
 */

import React, { useMemo } from 'react'
import { Autocomplete, Checkbox, ListItem, ListItemDecorator, Typography } from '@mui/joy'
import { LOAD_SOURCE_OPTIONS } from '../../utils/constants'
import { useHiddenBrokerSources } from '../../hooks/useHiddenBrokerSources'
import {
    TelegramIcon,
    ParadeIcon,
    HappyRobotIcon,
    TqlIcon,
    ItfIcon,
    PlsIcon,
    BeonIcon,
    NavisphereIcon,
    ArriveIcon,
    EmergeIcon,
    EchoIcon,
    SpotIcon,
    RxoIcon,
    ModeGlobalIcon,
    KnxIcon,
    SchneiderIcon,
    SylectusIcon,
    DatOneIcon,
    BrokerOutreachIcon,
    NumeoLoadsIcon,
} from '../../icons/SourceIcons'

export type SourceStatus = 'connected' | 'disconnected' | 'unknown' | 'loading'

export const SOURCE_ICONS: Record<string, React.ReactNode> = {
    TELEGRAM: <TelegramIcon sx={{ width: 14, height: 14 }} />,
    PARADE: <ParadeIcon sx={{ width: 14, height: 14 }} />,
    HAPPYROBOT: <HappyRobotIcon sx={{ width: 16, height: 12 }} />,
    TRUCKSTOP: <img src="/truckstop-logo.webp" alt="TruckStop" width={14} height={14} style={{ borderRadius: '2px', objectFit: 'contain' }} />,
    TQL: <TqlIcon />,
    ITF: <ItfIcon />,
    PLS: <PlsIcon />,
    BEON: <BeonIcon />,
    NAVISPHERE: <NavisphereIcon />,
    ARRIVE: <ArriveIcon />,
    EMERGE: <EmergeIcon />,
    ECHO: <EchoIcon />,
    SPOT: <SpotIcon />,
    RXO: <RxoIcon />,
    MODE_GLOBAL: <ModeGlobalIcon />,
    JB_HUNT: <span style={{ fontSize: '10px', fontWeight: 600, color: '#FFB81C', lineHeight: '14px' }}>JBH</span>,
    KNX: <KnxIcon />,
    SCHNEIDER: <SchneiderIcon />,
    SYLECTUS: <SylectusIcon />,
    DAT: <DatOneIcon size={14} />,
    DAT_EXTENSION: <DatOneIcon size={14} />,
    TRUCKSTOP_ACCOUNT: (
        <img src="/truckstop_account.jpg" alt="TruckStop Account" width={14} height={14} style={{ borderRadius: '2px', objectFit: 'contain' }} />
    ),
    UBER_FREIGHT: <img src="/uber_lb.png" alt="Uber Freight" width={14} height={14} style={{ borderRadius: '2px', objectFit: 'contain' }} />,
    CONVOY: <img src="/convoy.png" alt="Convoy" width={14} height={14} style={{ borderRadius: '2px', objectFit: 'contain' }} />,
    // Broker outreach — extracted from broker reply on a cold-outreach thread.
    BROKER_OUTREACH: <BrokerOutreachIcon sx={{ width: 14, height: 14 }} />,
    // Numeo Loads — published directly by brokers through Broker Portal.
    BROKER_PORTAL: <NumeoLoadsIcon sx={{ width: 14, height: 14 }} />,
}

interface SourceOption {
    value: string
    label: string
}

interface SourcesSelectProps {
    selectedSources: string[]
    onToggleSource: (source: string) => void
    sourceStatuses: Record<string, SourceStatus>
    onSourceClick?: (source: string) => void
    error?: boolean
    /**
     * Sources whose checkbox should render as disabled. Clicking them does
     * nothing; tooltip explains why. Used by the agentic hub to lock down
     * non-DAT sources when the user entered a multi-state / zone filter.
     */
    disabledSources?: string[]
    /** Hover message when a disabled source is pointed at. */
    disabledTooltip?: string
    /** Sources to exclude from the option list entirely (e.g., AI Hub hides DAT_EXTENSION). */
    excludeSources?: string[]
}

export const SourcesSelect: React.FC<SourcesSelectProps> = ({
    selectedSources,
    onToggleSource,
    sourceStatuses,
    onSourceClick,
    error,
    disabledSources,
    disabledTooltip,
    excludeSources,
}) => {
    const hiddenSources = useHiddenBrokerSources()
    const disabledSet = useMemo(() => new Set(disabledSources ?? []), [disabledSources])
    const excludeSet = useMemo(() => new Set(excludeSources ?? []), [excludeSources])
    const hiddenSet = useMemo(() => new Set(hiddenSources), [hiddenSources])
    const allOptions: SourceOption[] = useMemo(() => {
        // Numeo Loads (BROKER_PORTAL) is now available to everyone (the numeo-loads-enabled
        // preview flag was removed) — callers hide it, when they want to, via excludeSources.
        return LOAD_SOURCE_OPTIONS.filter((o) => !excludeSet.has(o.value) && !hiddenSet.has(o.value)).map((o) => ({
            value: o.value,
            label: o.label,
        }))
    }, [excludeSet, hiddenSet])

    const selectedOptions = useMemo(() => allOptions.filter((o) => selectedSources.includes(o.value)), [allOptions, selectedSources])

    const getStatusDot = (value: string) => {
        const status = sourceStatuses[value] || 'unknown'
        if (status === 'connected') return <span className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />
        if (status === 'disconnected') return <span className="w-2 h-2 rounded-full bg-orange-400 flex-shrink-0" />
        if (status === 'loading') return <span className="w-2 h-2 rounded-full bg-yellow-400 animate-pulse flex-shrink-0" />
        return <span className="w-2 h-2 rounded-full bg-gray-300 flex-shrink-0" />
    }

    return (
        <div className="relative" style={{ width: '200px' }}>
            <Autocomplete
                multiple
                disableClearable
                disableCloseOnSelect
                placeholder={selectedOptions.length === 0 ? 'Sources' : ''}
                value={selectedOptions}
                limitTags={0}
                options={allOptions}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                renderOption={(props, option, { selected }) => {
                    const status = sourceStatuses[option.value] || 'unknown'
                    const isConnected = status === 'connected'
                    const isSelectedAndConnected = selected && isConnected
                    const isDisabled = disabledSet.has(option.value)
                    const handleOptionClick = (e: React.MouseEvent<HTMLLIElement>) => {
                        e.preventDefault()
                        e.stopPropagation()
                        if (isDisabled) return
                        if (status === 'disconnected' && onSourceClick) {
                            onSourceClick(option.value)
                        } else {
                            onToggleSource(option.value)
                        }
                    }
                    return (
                        <ListItem
                            {...props}
                            key={option.value}
                            title={isDisabled ? disabledTooltip : undefined}
                            aria-disabled={isDisabled}
                            onClick={handleOptionClick}
                            className={`${(props as React.HTMLAttributes<HTMLLIElement>).className ?? ''} ${isDisabled ? 'opacity-50 pointer-events-none' : 'cursor-pointer'}`}
                        >
                            <ListItemDecorator>
                                <Checkbox
                                    checked={selected}
                                    readOnly
                                    disabled={isDisabled}
                                    sx={{
                                        pointerEvents: 'none',
                                        ...(isSelectedAndConnected && !isDisabled
                                            ? { '& .MuiCheckbox-checkbox': { borderColor: '#22c55e', backgroundColor: '#22c55e', color: '#fff' } }
                                            : {}),
                                    }}
                                />
                            </ListItemDecorator>
                            <div className="flex items-center gap-2 w-full" style={{ pointerEvents: 'none' }}>
                                <span className="flex items-center h-[14px] [&_svg]:w-3.5 [&_svg]:h-3.5 [&_img]:w-3.5 [&_img]:h-3.5">
                                    {SOURCE_ICONS[option.value] || null}
                                </span>
                                <Typography sx={{ fontSize: '12px', fontFamily: "'Inter', sans-serif", lineHeight: '1.4', flex: 1 }}>{option.label}</Typography>
                                {getStatusDot(option.value)}
                            </div>
                        </ListItem>
                    )
                }}
                renderTags={(tagValue) => (
                    <div className="inline-flex items-center gap-1 overflow-hidden">
                        <span className="px-1.5 py-0.5 rounded bg-sky-100 dark:bg-sky-900/30 text-[10px] font-medium text-sky-600 dark:text-sky-400 flex-shrink-0">
                            {tagValue.length} source{tagValue.length !== 1 ? 's' : ''}
                        </span>
                    </div>
                )}
                sx={{
                    width: '100%',
                    height: '32px',
                    minHeight: '32px',
                    borderWidth: '1px',
                    borderRadius: '6px',
                    borderColor: error ? '#ef4444' : 'var(--lh-border)',
                    background: 'var(--lh-surface)',
                    fontSize: '12px',
                    fontFamily: "'Inter', sans-serif",
                    boxShadow: 'none',
                    alignItems: 'center', // Vertically center the rendered tag chip in the 32px control
                    '--Input-focusedInsetShadow': 'none',
                    '--Input-focusedThickness': '1px',
                    '--Input-focusedHighlight': 'var(--lh-accent)',
                    '&:focus-within': {
                        boxShadow: 'none',
                        borderColor: 'var(--lh-accent)',
                    },
                    '& .MuiAutocomplete-input': {
                        minHeight: 'auto',
                        height: '32px',
                        fontFamily: "'Inter', sans-serif",
                        fontWeight: 500,
                        fontSize: '12px',
                        lineHeight: '100%',
                        padding: '0 8px',
                        boxSizing: 'border-box',
                    },
                    '& .MuiAutocomplete-input::placeholder': {
                        fontFamily: "'Inter', sans-serif",
                        fontWeight: 500,
                        fontSize: '12px',
                        opacity: 1,
                        color: 'var(--lh-fg-muted)',
                    },
                }}
                slotProps={{
                    listbox: {
                        sx: {
                            '& .MuiListItem-root': {
                                fontSize: '12px',
                                fontFamily: "'Inter', sans-serif",
                                padding: '4px 8px',
                            },
                        },
                    },
                }}
            />
        </div>
    )
}
